import * as Sentry from '@sentry/react';
import { SectionData } from '@superfunnel/types';
import Modal from '@superfunnel/ui-core/Modal';
import FieldTitle from '@superfunnel/ui-core/v2/FieldTitle';
import Input from '@superfunnel/ui-core/v2/Input';
import Label from '@superfunnel/ui-core/v2/Label';
import Switch from '@superfunnel/ui-core/v2/Switch';
import {
  ToggleGroup,
  ToggleGroupItem,
} from '@superfunnel/ui-core/v2/ToggleGroup';
import { useCallback, useContext, useState } from 'react';
import { uid } from 'uid';
import { AuthContext } from '../contexts/AuthContext';
import { UserContext } from '../contexts/UserContext';
import { createFavoriteSection } from '../services/SectionsService';

interface FavoriteSectionConfig {
  name: string;
  level: 'account' | 'campaign';
  saveContent: boolean;
  saveImages: boolean;
}

const createDefaultFavoriteSectionConfig = (): FavoriteSectionConfig => ({
  name: '',
  level: 'account',
  saveContent: true,
  saveImages: true,
});

interface Props {
  dynamicPageId: string;
  section?: Partial<SectionData>;
  open: boolean;
  onClose: () => void;
}

export default function AddFavoriteSectionModal({
  dynamicPageId,
  section,
  open,
  onClose,
}: Props) {
  const { firebaseUser } = useContext(AuthContext);
  const { selectedAccount } = useContext(UserContext);

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string>();

  const [favoriteSectionConfig, setFavoriteSectionConfig] =
    useState<FavoriteSectionConfig>(createDefaultFavoriteSectionConfig);

  const _onClose = useCallback(() => {
    setError(undefined);
    setLoading(false);
    setFavoriteSectionConfig(createDefaultFavoriteSectionConfig());
    onClose();
  }, [onClose]);

  const onAddClick = useCallback(() => {
    if (!open) return;

    if (!section || !section.type || !section.variant) {
      setError('Something went wrong. Please try again.');
      return;
    }

    if (!selectedAccount || !dynamicPageId) {
      setError('Something went wrong. Please try again.');
      return;
    }

    const newSection: Partial<SectionData> = {
      ...section,
    };

    if (!favoriteSectionConfig.saveContent) {
      delete newSection.title;
      delete newSection.tagline;
      delete newSection.subtitle;

      if ('text' in newSection) {
        delete newSection.text;
      }

      if ('description' in newSection) {
        delete newSection.description;
      }

      if ('items' in newSection) {
        delete newSection.items;
      }

      if ('features' in newSection) {
        delete newSection.features;
      }

      if ('testimonials' in newSection) {
        delete newSection.testimonials;
      }

      if ('pressMentions' in newSection) {
        delete newSection.pressMentions;
      }

      if ('logos' in newSection) {
        delete newSection.logos;
      }

      if ('cta' in newSection) {
        delete newSection.cta;
      }
    }

    if (!favoriteSectionConfig.saveImages) {
      if ('image' in newSection) {
        delete newSection.image;
      }
    }

    if ('formData' in newSection) {
      delete newSection.formData;
    }

    if ('formId' in newSection) {
      delete newSection.formId;
    }

    setLoading(true);
    createFavoriteSection(
      selectedAccount.id,
      favoriteSectionConfig.level === 'campaign' ? dynamicPageId : '',
      favoriteSectionConfig.name,
      { ...newSection, id: uid(12) },
      firebaseUser?.uid || '',
    )
      .then(() => {
        _onClose();
      })
      .catch((e) => {
        Sentry.captureException(e);
        setError(e.message);
      })
      .finally(() => {
        setLoading(false);
      });
  }, [
    open,
    section,
    selectedAccount,
    dynamicPageId,
    favoriteSectionConfig.saveContent,
    favoriteSectionConfig.saveImages,
    favoriteSectionConfig.level,
    favoriteSectionConfig.name,
    firebaseUser?.uid,
    _onClose,
  ]);

  return (
    <Modal
      open={open}
      onClose={_onClose}
      title="Add Section to Favorites"
      actions={[
        {
          title: 'Cancel',
          type: 'secondary',
          disabled: loading,
          onClick: _onClose,
        },
        {
          title: 'Create',
          type: 'primary',
          loading: loading,
          onClick: onAddClick,
        },
      ]}
    >
      <div className="mt-0 flex flex-col gap-4">
        <Input
          title="Name (Optional)"
          value={favoriteSectionConfig.name}
          onChange={(e) =>
            setFavoriteSectionConfig({
              ...favoriteSectionConfig,
              name: e.target.value,
            })
          }
        />

        <div>
          <FieldTitle className="mb-2" title={'Save to'} />

          <ToggleGroup
            type="single"
            variant="outline"
            value={favoriteSectionConfig.level}
            onValueChange={(value: FavoriteSectionConfig['level']) => {
              if (!value) return;
              setFavoriteSectionConfig({
                ...favoriteSectionConfig,
                level: value,
              });
            }}
          >
            <ToggleGroupItem value="account">Account</ToggleGroupItem>
            <ToggleGroupItem value="campaign">Dynamic Page</ToggleGroupItem>
          </ToggleGroup>
        </div>

        <div className="flex items-center space-x-3">
          <Switch
            checked={favoriteSectionConfig.saveContent}
            onCheckedChange={(checked) => {
              setFavoriteSectionConfig({
                ...favoriteSectionConfig,
                saveContent: checked,
              });
            }}
          />
          <Label className="text-sm font-medium">Save Content</Label>
        </div>

        <div className="flex items-center space-x-3">
          <Switch
            checked={favoriteSectionConfig.saveImages}
            onCheckedChange={(checked) => {
              setFavoriteSectionConfig({
                ...favoriteSectionConfig,
                saveImages: checked,
              });
            }}
          />
          <Label className="text-sm font-medium">Save Images</Label>
        </div>
      </div>
      {error ? (
        <p className="mt-4 w-full text-end text-sm text-red-600">{error}</p>
      ) : (
        <></>
      )}
    </Modal>
  );
}
